export interface PasswordPolicyRule {
  id: string;
  message: string;
  isValid: boolean;
}

export interface PasswordValidationResult {
  isValid: boolean;
  rules: PasswordPolicyRule[];
}

export class PasswordValidator {
  
  static validatePassword(password: string, username?: string): PasswordValidationResult {
    const rules: PasswordPolicyRule[] = [
      {
        id: 'length',
        message: 'Use 8 or more characters.',
        isValid: password.length >= 8
      },
      {
        id: 'mix',
        message: 'Passwords are case sensitive and must include a mix of letters, numbers, and special characters. Special characters allowed $ ! @ * ? |',
        isValid: this.hasMixOfCharacters(password)
      },
      {
        id: 'spaces',
        message: "Don't use blank spaces.",
        isValid: !/\s/.test(password)
      },
      {
        id: 'repeat',
        message: "Don't repeat the same letter, number, or special character more than twice.",
        isValid: !this.hasRepeatingCharacters(password)
      }
    ];

    if (username) {
      rules.push({
        id: 'username',
        message: "Don't use more than 3 consecutive characters of your username.",
        isValid: !this.hasConsecutiveUsernameChars(password, username)
      });
    }

    const isValid = rules.every(rule => rule.isValid);

    return {
      isValid,
      rules
    };
  }

  private static hasMixOfCharacters(password: string): boolean {
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[\$!@*?\|]/.test(password);
    const hasCaseMix = /[a-z]/.test(password) && /[A-Z]/.test(password);
    
    return hasLetter && hasNumber && hasSpecialChar && hasCaseMix;
  }

  private static hasRepeatingCharacters(password: string): boolean {
    for (let i = 0; i < password.length - 2; i++) {
      if (password[i] === password[i + 1] && password[i] === password[i + 2]) {
        return true;
      }
    }
    return false;
  }

  private static hasConsecutiveUsernameChars(password: string, username: string): boolean {
    if (!username || username.length < 4) return false;

    const passwordLower = password.toLowerCase();
    const usernameLower = username.toLowerCase();

    for (let i = 0; i <= usernameLower.length - 4; i++) {
      const substring = usernameLower.substring(i, i + 4);
      if (passwordLower.includes(substring)) {
        return true;
      }
    }

    return false;
  }

  static doPasswordsMatch(password: string, confirmPassword: string): boolean {
    return password === confirmPassword && password.length > 0;
  }
}
